/**
 * Zod Schemas for Document Dashboard
 *
 * Provides runtime validation schemas for folder management and document listing.
 *
 * @module schemas/dashboard.zod
 * @see SPEC-016 Requirements 1.1, 2.5
 */
import { z } from 'zod';
import { FOLDER_NAME_PATTERN, MAX_FOLDER_DEPTH } from '../types/folder';
// ============================================================================
// Folder Schemas
// ============================================================================
/**
 * Schema for folder names.
 * Must be 1-64 characters and not contain invalid characters: / \ : * ? " < > |
 *
 * @see SPEC-016 Requirement 2.5
 */
export const FolderNameSchema = z
    .string()
    .min(1, 'Folder name cannot be empty')
    .max(64, 'Folder name cannot exceed 64 characters')
    .regex(FOLDER_NAME_PATTERN, 'Folder name contains invalid characters');
/**
 * Schema for folder paths.
 * Must start with "/" for non-root paths, or be empty string for root.
 * Maximum depth is enforced by MAX_FOLDER_DEPTH.
 */
export const FolderPathSchema = z
    .string()
    .max(512, 'Folder path cannot exceed 512 characters')
    .refine((path) => {
    // Empty string is valid (root)
    if (path === '' || path === '/')
        return true;
    // Must start with /
    if (!path.startsWith('/'))
        return false;
    // Check each segment for invalid characters
    const segments = path.slice(1).split('/');
    return segments.every((seg) => seg.length > 0 && FOLDER_NAME_PATTERN.test(seg));
}, { message: 'Invalid folder path format' })
    .refine((path) => {
    if (path === '' || path === '/')
        return true;
    const segments = path
        .replace(/^\/|\/$/g, '')
        .split('/')
        .filter(Boolean);
    return segments.length <= MAX_FOLDER_DEPTH;
}, { message: `Folder path cannot exceed ${MAX_FOLDER_DEPTH} levels deep` });
/**
 * Schema for FolderId - folder identifier.
 */
export const FolderIdSchema = z.string().uuid();
/**
 * Schema for Folder entity.
 */
export const FolderSchema = z.object({
    folderId: FolderIdSchema,
    name: FolderNameSchema,
    path: FolderPathSchema,
    parentPath: z.string(),
    ownerId: z.string().uuid(),
    createdAt: z.number().int().positive(),
    updatedAt: z.number().int().positive(),
});
// ============================================================================
// Document List API Schemas
// ============================================================================
/**
 * Schema for sort field options.
 */
export const SortBySchema = z.enum(['updatedAt', 'createdAt', 'title']);
/**
 * Schema for sort order options.
 */
export const SortOrderSchema = z.enum(['asc', 'desc']);
/**
 * Schema for document list query parameters.
 *
 * @see SPEC-016 Requirement 1.1
 */
export const ListDocsQuerySchema = z.object({
    folderId: z.string().uuid().optional(),
    sortBy: SortBySchema.default('updatedAt'),
    sortOrder: SortOrderSchema.default('desc'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
    search: z.string().max(100).optional(),
});
/**
 * Schema for individual document list item.
 */
export const DocListItemSchema = z.object({
    docId: z.string().uuid(),
    title: z.string(),
    previewText: z.string().max(200),
    folderPath: z.string(),
    ownerId: z.string().uuid(),
    createdAt: z.number().int().positive(),
    updatedAt: z.number().int().positive(),
    isShared: z.boolean(),
});
/**
 * Schema for document list response.
 */
export const ListDocsResponseSchema = z.object({
    docs: z.array(DocListItemSchema),
    total: z.number().int().min(0),
    hasMore: z.boolean(),
});
// ============================================================================
// Folder CRUD API Schemas
// ============================================================================
/**
 * Schema for create folder request.
 */
export const CreateFolderRequestSchema = z.object({
    name: FolderNameSchema,
    parentPath: FolderPathSchema.default(''),
});
/**
 * Schema for create folder response.
 */
export const CreateFolderResponseSchema = z.object({
    folderId: FolderIdSchema,
    path: FolderPathSchema,
});
/**
 * Schema for rename folder request.
 */
export const RenameFolderRequestSchema = z.object({
    name: FolderNameSchema,
});
/**
 * Schema for rename folder response.
 */
export const RenameFolderResponseSchema = z.object({
    folderId: FolderIdSchema,
    oldPath: z.string(),
    newPath: z.string(),
    affectedDocs: z.number().int().min(0),
});
/**
 * Schema for delete folder response.
 */
export const DeleteFolderResponseSchema = z.object({
    folderId: FolderIdSchema,
    movedDocs: z.number().int().min(0),
});
// ============================================================================
// Document Move/Delete API Schemas
// ============================================================================
/**
 * Schema for move document request.
 */
export const MoveDocRequestSchema = z.object({
    targetFolderPath: FolderPathSchema,
});
/**
 * Schema for move document response.
 */
export const MoveDocResponseSchema = z.object({
    docId: z.string().uuid(),
    oldPath: z.string(),
    newPath: z.string(),
});
/**
 * Schema for delete document response (soft delete).
 */
export const DeleteDocResponseSchema = z.object({
    docId: z.string().uuid(),
    deletedAt: z.number().int().positive(),
});
//# sourceMappingURL=dashboard.zod.js.map